/**
 * useCategoryDetailHandlers Hook
 * 
 * Handles detail management operations (legacy category-level details and group details).
 */

import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { TransactionCategory, TransactionCategoryGroup, TransactionCategoryDetail } from "../../../types";
import { api } from "../../../services/api";

export interface UseCategoryDetailHandlersParams {
  workspaceId: string;
  selectedTab: "expense" | "income";
  categories: TransactionCategory[];
  groups: TransactionCategoryGroup[];
  setCategories: (categories: TransactionCategory[]) => void;
  setGroups: (groups: TransactionCategoryGroup[]) => void;
  // UI state setters
  setError: (error: string) => void;
  setSavingDetails: (saving: boolean) => void;
  setSelectDetailsDialogOpen: (open: boolean) => void;
  setGroupDetailsDialogOpen: (open: boolean) => void;
  setCategoryForSelectDetails: (category: TransactionCategory | null) => void;
  setEnabledDetails: (details: string[]) => void;
  setDefaultValues: (values: Record<string, string | number>) => void;
  setOriginalEnabledDetails: (details: string[]) => void;
  setOriginalDefaultValues: (values: Record<string, string | number>) => void;
  setEditingGroupDetails: (details: TransactionCategoryDetail[]) => void;
  setOriginalGroupDetails: (details: TransactionCategoryDetail[]) => void;
  setEditingGroupNameForDetails: (name: string) => void;
  setNewDetailName: (name: string) => void;
  setNewDetailType: (type: "list" | "measurable" | "") => void;
  setNewDetailUnit: (unit: string) => void;
  setNewListItems: (items: string[]) => void;
  setDetailNameError: (error: string) => void;
  setEditingDetailIndex: (index: number | null) => void;
  setEditingOriginalDetail: (detail: TransactionCategoryDetail | null) => void;
  setEditingDetails: (details: TransactionCategoryDetail[]) => void;
  // Current state values
  categoryForSelectDetails: TransactionCategory | null;
  enabledDetails: string[];
  defaultValues: Record<string, string | number>;
  editingGroupDetails: TransactionCategoryDetail[];
  editingGroupNameForDetails: string;
  editingDetails: TransactionCategoryDetail[];
  editingDetailIndex: number | null;
}

export interface UseCategoryDetailHandlersReturn {
  handleEditDetail: (index: number) => void;
  handleRemoveDetail: (index: number) => void;
  handleEditGroupDetail: (index: number) => void;
  handleRemoveGroupDetail: (index: number) => void;
  handleSaveGroupDetails: () => Promise<void>;
  handleSaveSelectDetails: () => Promise<void>;
  handleDetailDragEnd: (event: DragEndEvent) => void;
  handleGroupDetailDragEnd: (event: DragEndEvent) => void;
}

/**
 * Hook for category detail management handlers
 */
export function useCategoryDetailHandlers({
  workspaceId,
  selectedTab,
  categories,
  groups,
  setCategories,
  setGroups,
  setError,
  setSavingDetails,
  setSelectDetailsDialogOpen,
  setGroupDetailsDialogOpen,
  setCategoryForSelectDetails,
  setEnabledDetails,
  setDefaultValues,
  setOriginalEnabledDetails,
  setOriginalDefaultValues,
  setEditingGroupDetails,
  setOriginalGroupDetails,
  setEditingGroupNameForDetails,
  setNewDetailName,
  setNewDetailType,
  setNewDetailUnit,
  setNewListItems,
  setDetailNameError,
  setEditingDetailIndex,
  setEditingOriginalDetail,
  setEditingDetails,
  categoryForSelectDetails,
  enabledDetails,
  defaultValues,
  editingGroupDetails,
  editingGroupNameForDetails,
  editingDetails,
  editingDetailIndex,
}: UseCategoryDetailHandlersParams): UseCategoryDetailHandlersReturn {
  // Legacy detail drag end (for category-level details)
  const handleDetailDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = parseInt(String(active.id));
    const newIndex = parseInt(String(over.id));

    if (isNaN(oldIndex) || isNaN(newIndex)) {
      return;
    }

    // Reorder details
    const reorderedDetails = arrayMove(editingDetails, oldIndex, newIndex);
    setEditingDetails(reorderedDetails);
    
    // Update editingDetailIndex if it was affected
    if (editingDetailIndex !== null) {
      if (editingDetailIndex === oldIndex) {
        setEditingDetailIndex(newIndex);
      } else if (editingDetailIndex > oldIndex && editingDetailIndex <= newIndex) {
        setEditingDetailIndex(editingDetailIndex - 1);
      } else if (editingDetailIndex < oldIndex && editingDetailIndex >= newIndex) {
        setEditingDetailIndex(editingDetailIndex + 1);
      }
    }
  };

  // Handle edit detail - populate form fields (legacy category-level)
  const handleEditDetail = (index: number): void => {
    const detail = editingDetails[index];
    setEditingDetailIndex(index);
    setEditingOriginalDetail(detail);
    setNewDetailName(detail.name);
    setNewDetailType(detail.type);
    setNewDetailUnit(detail.unit || "");
    setNewListItems([...(detail.listItems || [])]);
    setDetailNameError("");
  };

  // Handle remove detail (legacy category-level)
  const handleRemoveDetail = (index: number): void => {
    setEditingDetails(editingDetails.filter((_, i) => i !== index));
    if (editingDetailIndex === index) {
      setEditingDetailIndex(null);
      setEditingOriginalDetail(null);
      setNewDetailName("");
      setNewDetailType("");
      setNewDetailUnit("");
      setNewListItems([]);
      setDetailNameError("");
    } else if (editingDetailIndex !== null && editingDetailIndex > index) {
      setEditingDetailIndex(editingDetailIndex - 1);
    }
  };

  // Group detail drag end
  const handleGroupDetailDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = parseInt(String(active.id));
    const newIndex = parseInt(String(over.id));

    if (isNaN(oldIndex) || isNaN(newIndex)) {
      return;
    }

    // Reorder group details
    const reorderedDetails = arrayMove(editingGroupDetails, oldIndex, newIndex);
    setEditingGroupDetails(reorderedDetails);
    
    // Update editingDetailIndex if it was affected
    if (editingDetailIndex !== null) {
      if (editingDetailIndex === oldIndex) {
        setEditingDetailIndex(newIndex);
      } else if (editingDetailIndex > oldIndex && editingDetailIndex <= newIndex) {
        setEditingDetailIndex(editingDetailIndex - 1);
      } else if (editingDetailIndex < oldIndex && editingDetailIndex >= newIndex) {
        setEditingDetailIndex(editingDetailIndex + 1);
      }
    }
  };

  // Handle edit group detail
  const handleEditGroupDetail = (index: number): void => {
    const detail = editingGroupDetails[index];
    setEditingDetailIndex(index);
    setEditingOriginalDetail(detail);
    setNewDetailName(detail.name);
    setNewDetailType(detail.type);
    setNewDetailUnit(detail.unit || "");
    setNewListItems([...(detail.listItems || [])]);
    setDetailNameError("");
  };

  // Handle remove group detail
  const handleRemoveGroupDetail = (index: number): void => {
    setEditingGroupDetails(editingGroupDetails.filter((_, i) => i !== index));
    if (editingDetailIndex === index) {
      setEditingDetailIndex(null);
      setEditingOriginalDetail(null);
      setNewDetailName("");
      setNewDetailType("");
      setNewDetailUnit("");
      setNewListItems([]);
      setDetailNameError("");
    } else if (editingDetailIndex !== null && editingDetailIndex > index) {
      setEditingDetailIndex(editingDetailIndex - 1);
    }
  };

  // Handle save category detail selection
  const handleSaveSelectDetails = async (): Promise<void> => {
    if (!categoryForSelectDetails || !workspaceId) {
      setError("Missing required information");
      return;
    }

    setSavingDetails(true);
    setError("");

    try {
      // Filter out empty default values (empty strings, null, undefined)
      // Only keep non-empty values so they can be properly used when creating transactions
      const cleanedDefaultValues: Record<string, string | number> = {};
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          cleanedDefaultValues[key] = value;
        }
      });
      
      // Update category with enabled details and default values
      await api.updateCategory(workspaceId, categoryForSelectDetails.id, undefined, undefined, undefined, undefined, enabledDetails, cleanedDefaultValues);
      
      // Refresh categories to get updated data
      const categoriesData = await api.getCategories(workspaceId, selectedTab);
      setCategories(categoriesData);
      
      setSelectDetailsDialogOpen(false);
      setCategoryForSelectDetails(null);
      setEnabledDetails([]);
      setDefaultValues({});
      setOriginalEnabledDetails([]);
      setOriginalDefaultValues({});
    } catch (err: any) {
      setError(err.message || "Failed to save detail selection");
    } finally {
      setSavingDetails(false);
    }
  };

  // Handle save group details
  const handleSaveGroupDetails = async (): Promise<void> => {
    if (!editingGroupNameForDetails || !workspaceId) {
      setError("Missing required information");
      return;
    }

    setSavingDetails(true);
    setError("");

    try {
      // Find the group by name
      const group = groups.find((g) => g.name === editingGroupNameForDetails);
      if (!group || !group.id || !group.name) {
        setError("Group not found or invalid");
        return;
      }

      // Validate editingGroupDetails before sending
      if (!Array.isArray(editingGroupDetails)) {
        console.error(`[handleSaveGroupDetails] editingGroupDetails is not an array:`, editingGroupDetails);
        setError("Invalid details data");
        return;
      }

      // Update group with details (only update details, not name or description)
      // The backend will automatically update all categories' defaultValues and enabledDetails
      // to use the new detail names if any detail names were changed
      await api.updateGroup(workspaceId, group.id, undefined, undefined, editingGroupDetails);
      
      // Small delay to ensure backend batch commit completes
      // (Backend updates categories in a batch, which is async)
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Refresh groups to get updated data
      const groupsData = await api.getGroups(workspaceId, selectedTab);
      setGroups(groupsData);
      
      // Refresh categories to get updated defaultValues with new detail names (if any were renamed)
      // The backend batch update should be complete by now
      const categoriesData = await api.getCategories(workspaceId, selectedTab);
      setCategories(categoriesData);
      
      setGroupDetailsDialogOpen(false);
      setEditingGroupDetails([]);
      setOriginalGroupDetails([]);
      setEditingGroupNameForDetails("");
      setNewDetailName("");
      setNewDetailType("");
      setNewDetailUnit("");
      setNewListItems([]);
      setDetailNameError("");
      setEditingDetailIndex(null);
      setEditingOriginalDetail(null);
    } catch (err: any) {
      setError(err.message || "Failed to save group details");
    } finally {
      setSavingDetails(false);
    }
  };

  return {
    handleEditDetail,
    handleRemoveDetail,
    handleEditGroupDetail,
    handleRemoveGroupDetail,
    handleSaveGroupDetails,
    handleSaveSelectDetails,
    handleDetailDragEnd,
    handleGroupDetailDragEnd,
  };
}
